import React, { useState, useEffect } from 'react';
import { api, trackEvent } from './shared/utils/api';
import { ArrowRight, ArrowLeft, Close } from './Icons';

const TOUR_STEPS = [
  {
    tab: 'dashboard',
    title: 'Your priority dashboard',
    text: 'This ranks your topics by urgency. Weak topics rise to the top, and areas that carry more of the exam score get an extra boost. As your exam gets closer the list shifts to reflect what matters most. Check it before every session.',
    hint: 'Looking at the dashboard now',
  },
  {
    tab: 'flk1',
    title: 'Rate your confidence on each topic',
    text: 'Click any topic row to expand it, then drag the slider to rate your confidence. It saves instantly. The revision history below each topic builds up as you study. You\'ll see your progress over time.',
    hint: 'On your FLK1 topics',
  },
  {
    tab: 'sra',
    title: '220 official SRA questions',
    text: 'Choose Practice mode for instant feedback after each question, or Exam mode to simulate real exam conditions with a timer. Your results feed into your priority dashboard either way.',
    hint: 'On SRA Questions',
  },
  {
    tab: 'mcqresults',
    title: 'Import MCQ results',
    text: 'If you use Law Drills, paste your session results here. Select your provider, paste the results, and review the topic matches before confirming. SQETrack maps each score to the right FLK topic. More connections coming.',
    hint: 'On MCQ Results',
  },
  {
    tab: 'timetable',
    title: 'Weekly revision planner',
    text: 'The numbers show how many topics to cover this week to stay on pace. Your preparation target — set in Settings — determines when "strong" means strong enough. Hit auto-fill to build a week from your priority topics. Drag blocks to rearrange, then export to calendar.',
    hint: 'On the Planner',
  },
  {
    tab: 'recall',
    title: 'Active Recall Blurt',
    text: 'Test yourself on any topic. Pick one, write down everything you remember in 3 minutes, then compare against the detail. Your self-assessment adjusts your confidence score for that topic.',
    hint: 'On Recall Blurt',
  },
  {
    tab: 'progress',
    title: 'Track your progress',
    text: 'Your confidence by module over time. This chart builds as you rate topics and import results. Check it weekly to see what\'s improving and what needs more attention.',
    hint: 'On Progress',
  },
  {
    tab: 'settings',
    title: 'Customise your setup',
    text: 'Set your exam dates, study provider, daily MCQ target, and preparation target. Your preparation target controls what "ready" means in the planner — raise it if you want a higher confidence bar before exam day.',
    hint: 'On Settings',
  },
  {
    tab: 'feedback',
    title: 'Help us improve',
    text: 'Found a bug or have a feature request? Use the feedback tab to tell us. We read everything and respond to every report.',
    hint: 'On Feedback',
  },
];

export default function TourOverlay({ setTab, onDone }) {
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);
  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;

  useEffect(() => {
    setTab(current.tab);
  }, [step]); // eslint-disable-line

  const next = () => {
    if (isLast) finish(false);
    else setStep(s => s + 1);
  };

  const finish = async (skipped = false) => {
    setExiting(true);
    await api('/api/settings', { method: 'POST', body: JSON.stringify({ tour_shown: '1' }) });
    trackEvent(skipped ? 'tour_skipped' : 'tour_completed', { steps_seen: step + 1 });
    onDone();
  };

  return (
    <div className={`tour-backdrop ${exiting ? 'tour-exiting' : ''}`}>
      <div className="tour-panel">
        <div className="tour-panel-top">
          <span className="tour-step-pill">{step + 1} / {TOUR_STEPS.length}</span>
          <button className="tour-close-btn" onClick={() => finish(true)} title="Skip tour">
            <Close size={12} />
          </button>
        </div>
        <div className="tour-hint">{current.hint}</div>
        <div className="tour-panel-title">{current.title}</div>
        <div className="tour-panel-text">{current.text}</div>
        <div className="tour-panel-footer">
          <div className="tour-dots">
            {TOUR_STEPS.map((_, i) => (
              <span key={i} className={`tour-dot ${i === step ? 'active' : i < step ? 'done' : ''}`} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 0 && (
              <button className="tour-back-btn" onClick={() => setStep(s => s - 1)}>
                <ArrowLeft size={12} /> Back
              </button>
            )}
            <button className="tour-next-btn" onClick={next}>
              {isLast ? 'Finish tour' : <>Next <ArrowRight size={12} /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
